import React from "react";
import { useMemo, useRef, useEffect, useState } from "react";
// Components
import DateRangePicker from "../DateSelector/DateSelector";
import DataTable from "../Common/components/DataTable/DataTable";
import { createColumnHelper } from "@tanstack/react-table";
import { Loader } from "rsuite";
import BaseCell from "../Common/components/BaseCell";
import ArrowDown from "../../assets/images/bluedownarrow.svg";
import SkeletonDatable from "../SkeletonComponents/SkeletonDatable";
import TimeCell from "../../utilities/TimeCell";
import BlueArrow from "../../assets/images/small-back.svg";
import SkeletonEmptyDialogue from "../SkeletonComponents/SkeletonEmptyDialogue";
import { DateRangePicker as CustomDateRangePicker } from "rsuite";
import BulkCapabilitiesForm from "./BulkCapabilitiesForm/BulkCapabilitiesForm";
// api config
import Axios from "axios";
import { APIConfig } from "../../services/apiConfiguration";
import apiEndpointList from "../../config/core_banking/endpoint";
// Utils
import HandleOutsideClick from "../Common/HandleOutsideClick/HandleOutsideClick";
import ShowBlueValue from "../VirtualAccountDataTable/ShowBlueValue";
// Contexts
import { BulkCapabilitiesContextProvider } from "./Contexts/BulkCapabilitiesContext";
// posthog imports
import posthog from "posthog-js";
import { capturePosthogEvent } from "../../utilities/posthogUtils";
// style imports
import "../Common/ReactDataTable.scss";

const CancelToken = Axios.CancelToken;
const source = CancelToken.source();

const BulkCapabilities = () => {
  // Posthog event capture on page visit
  useEffect(() => {
    if (
      process.env.REACT_APP_ENV_NAME === "staging" ||
      process.env.REACT_APP_ENV_NAME === "production"
    ) {
      posthog.capture("Bulk Capabilities View", {
        path: window.location.origin + window.location.pathname,
        customer_name: JSON.parse(localStorage.getItem("user")).client_id,
        user_email: JSON.parse(localStorage.getItem("user")).email,
      });
    }
  }, []);

  const [filteredOptions, setFilteredOptions] = useState({});
  const [accountnumber, setAccountNumber] = useState();
  const [bulkPayoutCTA, setBulkPayoutCTA] = useState(false);

  // const [uiUpdate, setUiUpdate] = useState(false);

  //& Allow payouts API call
  const allowInitiatePayoutApi = () => {
    APIConfig.API_Client.post(
      apiEndpointList.ALLOW_PAYMENTS_PAYOUTS.baseUrl +
        apiEndpointList.ALLOW_PAYMENTS_PAYOUTS.endpoint,
      { client_id: JSON.parse(localStorage.getItem("user")).client_id },
      { cancelToken: source.token }
    )
      .then((response) => {
        setBulkPayoutCTA(response.data[0].enabled);
      })
      .catch((error) => {});
  };

  useEffect(() => {
    if (process.env.REACT_APP_ENV_NAME === "production") {
      allowInitiatePayoutApi();
    }

    if (
      process.env.REACT_APP_ENV_NAME === "staging" ||
      process.env.REACT_APP_ENV_NAME === "qa"
    ) {
      setBulkPayoutCTA(true);
    }
  }, []);

  const handleFilter = (key, val) => {
    setFilteredOptions((old) => {
      let newVal = { ...old };
      newVal[key] = val;
      return newVal;
    });
  };
  const {
    setDateFilters,
    setfirstApiCall,
    setFilterList,
    setInitialLoading,
    setTableData,
    setTotalCount,
    fetchData,
    totalCount,
    tableData,
    filterList,
    initialLoading,
    dateFilters,
    showTransactions,
    setShowTransactions,
    setSelectedTransaction,
    isLoading,
    noDataFound,
    setNoDataFound,
    lastApiTime,
    showModalCVA,
    setShowModalCVA,
    lastInterval,
    refreshing,
    setIsRefreshing,
    isError,
    errorResponse,
    setLastApiTime,
    firstApiCall,
    refreshTable,
    setDateSearch,
    uiUpdate,
    setUiUpdate,
    setChildPayload,
    childPayload,
    childData,
    urnToBulkProcessingTypeMap,
  } = React.useContext(BulkCapabilitiesContextProvider);

  const viewTransactions = (decentro_company_urn, params) => {
    setChildPayload((prev) => ({
      ...prev,
      urn: params.decentro_company_urn,
      type: params.bulk_processing_type,
      status: params.status,
    }));
    setUiUpdate(true);
    setSelectedTransaction(decentro_company_urn);
    setAccountNumber(decentro_company_urn);
    toggleTransactions(true);
    setFilteredOptions({});
    setSortState({
      sortBy: null,
      sortOrder: null,
    });
  };
  // setDateSearch(false);
  const [sortState, setSortState] = useState({
    sortBy: null,
    sortOrder: null, // true == asc, false === desc, undefined == nosort
  });
  function getSortResult(a, b, key) {
    return a[key]?.toString().localeCompare(b[key], undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }
  function headerFn(key) {
    setSortState(({ sortBy, sortOrder }) => {
      if (sortBy === key) {
        return {
          sortBy,
          sortOrder:
            sortOrder === null ? false : sortOrder === false ? true : null,
        };
      } else {
        return {
          sortBy: key,
          sortOrder: false,
        };
      }
    });
  }
  const columnList = [
    {
      name: "Decentro URN",
      accessor: "decentro_company_urn",
      clickFn: viewTransactions,
      cell: ShowBlueValue,
      showInitial: true,
    },
    {
      name: "Bulk Processing Type",
      accessor: "bulk_processing_type",
      showInitial: true,
      hasPopup: true,
    },
    {
      name: "Created On",
      accessor: "date_time",
      showInitial: true,
      cell: TimeCell,
    },
    {
      name: "Status",
      accessor: "status",
      showInitial: true,
      hasPopup: true,
    },
    {
      name: "Total Records",
      accessor: "total_records",
      showInitial: true,
      cell: (row) => (row?.getValue() === null ? "-" : row?.getValue()),
    },
    {
      name: "Processed Records",
      accessor: "processed_records",
      showInitial: true,
      cell: (row) => (row?.getValue() === null ? "-" : row?.getValue()),
    },
    {
      name: "Success Records",
      accessor: "success_records",
      showInitial: true,
      cell: (row) => (row?.getValue() === null ? "-" : row?.getValue()),
    },
    {
      name: "Failure Records",
      accessor: "failure_records",
      showInitial: true,
      cell: (row) => (row?.getValue() === null ? "-" : row?.getValue()),
    },
  ];
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  const transactionColumnList = [
    {
      accessor: "urn",
      name: "URN",
      showInitial: true,
    },
    {
      accessor: "created_on",
      name: "Created On",
      showInitial: true,
      cell: TimeCell,
    },
    {
      accessor: "type",
      name: "Type",
      showInitial: true,
      hasPopup: true,
    },
    {
      accessor: "description",
      name: "Description",
      hasPopup: true,
      showInitial: true,
    },
    {
      accessor: "status",
      name: "Status",
      hasPopup: true,
      showInitial: true,
    },
  ];
  const columnHelper = createColumnHelper();
  const [toggleEntries, setToggleEntries] = useState(false);
  const [toggleColumn, setToggleColumn] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const showCsvModal = () => {
    setShowModal(true);
  };
  function showConsumerId(id, original) {
    setShowPopup(true);
    setSelectedAccount(original);
  }
  const hideCsvModal = () => {
    setShowModal(false);
  };

  const [columnsToShow, setColumnsToShow] = useState(
    columnList.map((x) => x.name)
  );
  const columns = useMemo(() => {
    const arr = showTransactions ? transactionColumnList : columnList;
    return arr
      .filter((x) => columnsToShow.includes(x.name))
      .map((x) =>
        columnHelper.accessor(x?.accessor ?? x.name, {
          cell: x?.cell ?? BaseCell,
          header: x?.header ?? x.name,
          enableSorting: x.enableSorting ?? true,
          clickFn: x.clickFn ?? null,
          filterFn: x.filterFn,
          hasPopup: x.hasPopup,
          headerFn: x.enableSorting !== false ? headerFn : undefined,
          sortState:
            sortState.sortBy === x.accessor
              ? sortState.sortOrder === false
                ? "desc"
                : sortState.sortOrder === true
                ? "asc"
                : undefined
              : undefined,
          ...x,
        })
      );
  }, [columnsToShow, sortState]);

  const searchAllTransactions = (term) => {
    let noDataPresentForSearch = false;
    let searchData;

    if (term !== "") {
      searchData = dataToShow.filter((x) => {
        const string = term.toLowerCase();
        setNoDataFound(false);
        return JSON.stringify(x).toLowerCase().includes(string);
      });
    }

    if (searchData.length === 0) {
      setfirstApiCall(true);
      setIsRefreshing(true);
      setNoDataFound(false);
      fetchData(
        1,
        {
          ...filterList,
          currentPage: 1,
          search: term,
        },
        undefined,
        undefined,
        false
      );
    }
    return noDataPresentForSearch;
  };

  const initializeCsv = () => {
    const csvHeaders = columnsToShow.join(",");
    const columnData = showTransactions ? transactionColumnList : columnList;
    const dataTypes = columnData.filter((x) => columnsToShow.includes(x.name));

    const rows = dataToShow.map((x) => {
      return dataTypes.map((y) => x[y.accessor]).join(",");
    });
    const csvData =
      "data:text/csv;charset=utf-8," + [csvHeaders, ...rows].join("\n");
    const encodedUri = encodeURI(csvData);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "table-data.csv");
    document.body.appendChild(link);
    link.click();
    hideCsvModal();
  };

  const { dataToShow, maxPages, startIndex, endIndex, possibleOptions } =
    useMemo(() => {
      let data = tableData;
      if (filterList.search !== "") {
        data = data.filter((x) => {
          const string = filterList.search.toLowerCase();
          if (JSON.stringify(x).toLowerCase().includes(string)) {
            setNoDataFound(false);
            return JSON.stringify(x).toLowerCase().includes(string);
          } else {
            setNoDataFound(true);
          }
        });
      }

      let possibleOptions = {};
      if (data?.length > 0) {
        possibleOptions = Object.keys(data[0]).reduce((old, newVal) => {
          let newObj = { ...old };
          newObj[newVal] = data
            .map((item) => item[newVal])
            .filter((item, index, arr) => arr.indexOf(item) === index);
          return newObj;
        }, {});
      }

      Object.keys(filteredOptions).forEach((item) => {
        if (filteredOptions[item]) {
          data = data.filter(
            (fullItem) => fullItem[item] === filteredOptions[item]
          );
        }
      });
      if (sortState.sortBy !== null && sortState.sortOrder !== null) {
        data = data.sort((a, b) => {
          const key = sortState.sortBy;
          if (sortState.sortOrder === false) {
            return getSortResult(a, b, key) * -1;
          }
          if (sortState.sortOrder === true) {
            return getSortResult(a, b, key);
          }
        });
      }
      const maxPages = Math.ceil(data.length / filterList.resultsPerPage);
      const startIndex =
        (filterList.currentPage - 1) * filterList.resultsPerPage;
      const endIndex = startIndex + filterList.resultsPerPage;

      const paginatedData = data?.filter(
        (x, i) => i >= startIndex && i < endIndex
      );
      setTotalCount(possibleOptions?.decentro_company_urn?.length ?? 0);

      return {
        dataToShow: paginatedData,
        maxPages,
        startIndex,
        endIndex,
        possibleOptions,
      };
    }, [tableData, filterList, noDataFound, sortState, filteredOptions]);

  const allColumns = useMemo(
    () => (showTransactions ? transactionColumnList : columnList),
    [showTransactions]
  );

  const toggleEntrie = (e) => {
    if (typeof e === "boolean") {
      setToggleEntries(e);
      return;
    }
    setToggleEntries(!toggleEntries);
  };
  const toggleColum = (e) => {
    if (typeof e === "boolean") {
      setToggleColumn(e);
      return;
    }
    setToggleColumn(!toggleColumn);
  };
  const handleColumnCheck = (name) => {
    if (columnsToShow.includes(name)) {
      const index = columnsToShow.findIndex((x) => x === name);
      setColumnsToShow((prev) => {
        const items = [...prev];
        items.splice(index, 1);
        return [...items];
      });
    } else {
      setColumnsToShow([...columnsToShow, name]);
    }
  };
  useEffect(() => {
    // setUiUpdate(false);
    if (showTransactions) {
      toggleTransactions(true);
    }
  }, []);

  function Timer() {
    useEffect(() => {}, [lastApiTime]);
    return (
      <>
        <div className="timer" style={{ fontSize: 11 }}>
          {/* Updated : {lastApiTime} Min ago */}
          Refreshed {lastApiTime} Min ago
        </div>
      </>
    );
  }
  // Store the account number in the state and then call the second api using a single table - To be tried and checked
  const toggleTransactions = (e) => {
    setfirstApiCall(true);
    setInitialLoading(true);
    setNoDataFound(false);
    setTotalCount(0);
    setShowTransactions(e);
    setFilterList({
      ...filterList,
      currentPage: 1,
      search: "",
    });
    setSortState({
      sortBy: null,
      sortOrder: null, // true == asc, false === desc, undefined == nosort
    });

    setTableData([]);

    if (e) {
      setColumnsToShow(
        transactionColumnList
          .filter((item) => item.showInitial)
          .map((x) => x.name)
      );
      return;
    }
    setColumnsToShow(
      columnList.filter((item) => item.showInitial).map((x) => x.name)
    );
  };
  function clearAndShowModal() {
    setShowModalCVA(true);
  }
  function refreshtable() {
    // setSelectedAccount(null)
    setShowModalCVA(false);
    setNoDataFound(false);
    setInitialLoading(true);
    // setLastApiTime(0)
    clearInterval(lastInterval);
    setFilterList({ ...filterList, currentPage: 1 });
    fetchData(1, { ...filterList, currentPage: 1 }, undefined, true);
  }
  const divRef = useRef(null);
  const [isMounted, setIsMounted] = useState(true);

  useEffect(() => {
    fetchData(tableData.length + 1, undefined, undefined, undefined, false);
  }, [showTransactions]);

  useEffect(() => {
    refreshTable();
  }, []);

  useEffect(() => {
    if (tableData.length >= totalCount && tableData.length !== 0) return;
    const prefetchLimit = 200;
    const endIndex = filterList.currentPage * filterList.resultsPerPage;
    const limitConstant =
      Math.ceil((endIndex + 1) / prefetchLimit) * prefetchLimit +
      2 * filterList?.resultsPerPage;
    if (totalCount > 0) {
      if (tableData.length < limitConstant && firstApiCall === false) {
        fetchData(tableData.length + 1, undefined, undefined, undefined, false);
      }
    } else if (
      totalCount === 0 &&
      tableData.length === 0 &&
      firstApiCall === true
    ) {
      fetchData(tableData.length + 1, undefined, undefined, undefined, false);
    }
  }, [filterList.currentPage]);

  return (
    <>
      {!isError ? (
        <>
          {showModalCVA ? (
            <BulkCapabilitiesForm
              goBackToTable={refreshtable}
              setShowModalCVA={setShowModalCVA}
            />
          ) : (
            // <CreateVirtualAccount
            //     goBackToTable={refreshtable}
            //     setShowModalCVA={setShowModalCVA}
            // />
            <div className="ButtonWrapper">
              <div
                style={{
                  // overflowX: "scroll",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <>
                  <div className="total-count">
                    {/* {!uiUpdate && ( */}
                    {showTransactions == false ? (
                      <div className="title">
                        Bulk Capabilities{" "}
                        {isLoading ? (
                          <Loader
                            className="loader default ml-2 mt-2"
                            size="sm"
                          />
                        ) : (
                          <span className="entriesFoundContainer">
                            {totalCount} Entries Found
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="title">
                        <span
                          className="text-primary cursor-pointer"
                          onClick={() => {
                            toggleTransactions(false);
                            setUiUpdate(false);
                            setShowTransactions(false);
                            setFilterList({
                              search: "",
                              resultsPerPage: 10,
                              currentPage: 1,
                            });

                            filterList.search = "";
                            filterList.resultsPerPage = 10;
                            filterList.currentPage = 1;
                            // ! commented out because it hits va fetch api again
                            // fetchData(
                            //   tableData.length + 1,
                            //   undefined,
                            //   undefined,
                            //   undefined,
                            //   false
                            // );
                          }}
                        >
                          <img src={BlueArrow} alt="T" />
                        </span>
                        Transactions Summary for
                        <div className="text-primary-number">
                          {accountnumber}
                        </div>
                        {isLoading ? (
                          <Loader
                            className="loader default ml-2 mt-2"
                            size="sm"
                          />
                        ) : (
                          <span>{totalCount} Entries Found</span>
                        )}
                      </div>
                    )}
                    {/* )} */}
                    {/* {uiUpdate && ( */}

                    {/* )} */}
                    {/* {!uiUpdate && ( */}
                    {/* Showing CVA button only where required. */}
                    {showTransactions == false && (
                      <span>
                        {bulkPayoutCTA ? (
                          <button
                            className={
                              isLoading ? "btn-loading" : "virtual-accounts-btn"
                            }
                            onClick={() => {
                              if (!isLoading) {
                                if (
                                  process.env.REACT_APP_ENV_NAME ===
                                    "staging" ||
                                  process.env.REACT_APP_ENV_NAME ===
                                    "production"
                                ) {
                                  capturePosthogEvent(
                                    window.location.origin,
                                    window.location.pathname,
                                    "cta"
                                  );
                                }
                                clearAndShowModal();
                              }
                            }}
                          >
                            Bulk Upload
                          </button>
                        ) : (
                          ""
                        )}
                      </span>
                    )}
                    {/* )} */}
                  </div>
                  <div className="filter-data-wrapper">
                    <div className="search-filter-wrapper">
                      <input
                        type="search"
                        placeholder="Search"
                        value={filterList.search}
                        onChange={(e) => {
                          setNoDataFound(false);
                          let val = e.target.value;
                          if (
                            e.target.value === "" &&
                            dataToShow.length === 0
                          ) {
                            // if (uiUpdate) {
                            //   setShowTransactions(true);
                            // } else {
                            //   setShowTransactions(false);
                            // }
                            setIsRefreshing(true);
                            fetchData(
                              1,
                              {
                                ...filterList,
                                currentPage: 1,
                                search: e.target.value,
                              },
                              undefined,
                              true,
                              false
                            );
                          } else if (e.target.value.length > 4) {
                            // if (uiUpdate) {
                            //   setShowTransactions(true);
                            // } else {
                            //   setShowTransactions(false);
                            // }
                            setTimeout(() => {
                              searchAllTransactions(val);
                            }, 300);
                          }
                          setFilterList({
                            ...filterList,
                            currentPage: 1,
                            search: e.target.value,
                          });
                        }}
                      />
                    </div>
                    <div id="datecont-wrapper">
                      <div>
                        {isLoading ? (
                          <CustomDateRangePicker
                            format="DD MMM 'YY"
                            cleanable={false}
                            value={dateFilters}
                            disabled={true}
                            appearance="default"
                          />
                        ) : (
                          <DateRangePicker
                            selectedDateRange={dateFilters}
                            format="DD MMM 'YY"
                            restrictedDateFlag={true}
                            cleanable={false}
                            setSelectedDateRange={(e) => {
                              setDateSearch(true);
                              // settotalcountNotset(true);
                              setNoDataFound(false);
                              setTableData([]);
                              setDateFilters(e);
                              setInitialLoading(true);
                              clearInterval(lastInterval);
                              setLastApiTime(0);
                              setFilterList({ ...filterList, currentPage: 1 });
                              setfirstApiCall(true);
                              if (uiUpdate) {
                                setShowTransactions(true);
                                fetchData(
                                  1,
                                  { ...filterList, currentPage: 1 },
                                  e,
                                  undefined,
                                  false
                                );
                              } else {
                                setShowTransactions(false);
                                fetchData(
                                  1,
                                  { ...filterList, currentPage: 1 },
                                  e,
                                  undefined,
                                  false
                                );
                              }
                            }}
                          />
                        )}
                      </div>
                    </div>
                    <div
                      className={
                        isLoading
                          ? "show-entries-wrapper-main-loading"
                          : "show-entries-wrapper-main"
                      }
                    >
                      <HandleOutsideClick
                        onClickOutside={() => toggleEntrie(false)}
                      >
                        <div className="entries-wrapper-main">
                          <button onClick={isLoading ? () => {} : toggleEntrie}>
                            {filterList.resultsPerPage} Entries
                            <img
                              className="entries-dropdown-icon"
                              src="/images/arrow-down.svg"
                              style={
                                isLoading
                                  ? {
                                      filter:
                                        "brightness(135%) sepia(100%) hue-rotate(200deg)",
                                    }
                                  : {}
                              }
                            />
                          </button>
                          <div
                            onMouseLeave={toggleEntrie}
                            className={toggleEntries ? "block-main" : "hidden"}
                          >
                            <ul>
                              {[10, 20, 30, 50].map((entr) => (
                                <li
                                  onClick={() => {
                                    setFilterList({
                                      ...filterList,
                                      resultsPerPage: entr,
                                      currentPage: 1,
                                    });
                                    toggleEntrie();
                                  }}
                                  key={entr}
                                >
                                  {entr}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </HandleOutsideClick>
                    </div>
                    <div
                      className={
                        isLoading
                          ? "show-column-wrapper-main-loading"
                          : "show-column-wrapper-main"
                      }
                    >
                      <HandleOutsideClick
                        onClickOutside={
                          isLoading ? () => {} : () => toggleColum(false)
                        }
                      >
                        <div className="column-wrapper">
                          <button onClick={isLoading ? () => {} : toggleColum}>
                            {/* Manage Column */}
                            Columns
                            <span>
                              <img
                                src={ArrowDown}
                                alt="Arrow Down"
                                style={
                                  isLoading
                                    ? {
                                        filter:
                                          "brightness(135%) sepia(100%) hue-rotate(200deg)",
                                      }
                                    : {}
                                }
                              />
                            </span>
                          </button>
                          <div
                            onMouseLeave={toggleColum}
                            className={toggleColumn ? "block" : "hidden"}
                          >
                            <ul>
                              {allColumns.map((colm) => {
                                return (
                                  <li key={colm.name}>
                                    <label htmlFor={colm.name}>
                                      {/* <span className="checkboxIcon">
                                          {columnsToShow.includes(
                                            colm.name
                                          ) && (
                                            <span className="checkIcon">✔</span>
                                          )}
                                          <input
                                            hidden
                                            onChange={() =>
                                              handleColumnCheck(colm.name)
                                            }
                                            checked={columnsToShow.includes(
                                              colm.name
                                            )}
                                            id={colm.name}
                                            type="checkbox"
                                          />
                                        </span> */}
                                      <span>{colm.name}</span>
                                      <input
                                        type="checkbox"
                                        onChange={() =>
                                          handleColumnCheck(colm.name)
                                        }
                                        checked={columnsToShow.includes(
                                          colm.name
                                        )}
                                        id={colm.name}
                                      />
                                    </label>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>
                      </HandleOutsideClick>
                    </div>
                    <div
                      className={
                        isLoading
                          ? "download-csv-main-loading"
                          : "download-csv-main"
                      }
                    >
                      {/* <button onClick={isLoading ? () => {} : showCsvModal}>
                        Download
                      </button> */}
                    </div>
                    <div className="ml-auto self-center">{/* <Timer /> */}</div>
                    <div
                      className={
                        isLoading
                          ? "download-csv-main-disable"
                          : `download-csv-main ${
                              refreshing && "refreshing-btn"
                            }`
                      }
                    >
                      <button
                        onClick={() => {
                          if (!isLoading) {
                            setNoDataFound(false);
                            setIsRefreshing(true);
                            clearInterval(lastInterval);
                            setLastApiTime(0);
                            setfirstApiCall(true);
                            setFilterList({ ...filterList, currentPage: 1 });
                            fetchData(
                              1,
                              { ...filterList, currentPage: 1 },
                              undefined,
                              true
                            );
                          }
                        }}
                      >
                        {refreshing || isLoading ? (
                          <>
                            <span>
                              <img
                                src="/images/Refresh-green.svg"
                                alt="refresh icon"
                              />
                            </span>
                            Fetching Data..
                          </>
                        ) : (
                          <>
                            <span>
                              <img
                                src="/images/refresh-icon.svg"
                                alt="refresh icon"
                              />
                            </span>
                            {/* Refresh Table */}
                            <Timer />
                          </>
                        )}
                      </button>
                    </div>
                    {showModal && (
                      <div
                        id="myModal"
                        className="modal"
                        onClick={hideCsvModal}
                      >
                        <div className="modal-content">
                          <div className="modal-header">
                            <p>Download CSV</p>
                            <span className="close"></span>
                          </div>
                          <div className="modal-body">
                            <p className="main_msg">Please note !</p>
                            <p>
                              Only the entries displayed in the table will be
                              downloaded.
                            </p>
                          </div>
                          <div className="button-holder">
                            <button onClick={hideCsvModal} className="cancel">
                              Cancel
                            </button>
                            <button
                              onClick={initializeCsv}
                              className="button_container_csv"
                            >
                              Download Now
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <DataTable
                    optionList={possibleOptions}
                    columns={columns}
                    filteredOptions={filteredOptions}
                    setFilter={handleFilter}
                    data={isLoading ? null : dataToShow}
                    enablePagination
                    isLoading={isLoading}
                    pageIndex={filterList?.currentPage}
                    totalMaxPages={Math.ceil(
                      totalCount / filterList?.resultsPerPage
                    )}
                    maxPages={maxPages}
                    refreshing={refreshing}
                    setPageIndex={(e) => {
                      if (
                        e === maxPages - 2 ||
                        e === maxPages - 1 ||
                        e === maxPages
                      ) {
                        setfirstApiCall(false);
                      } else {
                        setfirstApiCall(true);
                      }
                      setFilterList({
                        ...filterList,
                        currentPage: e,
                      });
                    }}
                  />
                </>
              </div>
            </div>
          )}
        </>
      ) : (
        <SkeletonEmptyDialogue type={errorResponse} />
      )}
    </>
  );
};

export default BulkCapabilities;
